import React, { forwardRef, useEffect, useState } from "react";
import Sparkles from "./Sparkles";
import { Crown, Sparkles as SparklesIcon } from "lucide-react";
import { fileUrl, getKingdomInfo } from "../lib/api";

const CASTLE_BG =
  "https://images.unsplash.com/photo-1766191395451-91c79c6ed395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtYWdpY2FsJTIwZmFpcnklMjB0YWxlJTIwY2FzdGxlJTIwcGluayUyMHNreXxlbnwwfHx8fDE3ODE1OTQ1OTd8MA&ixlib=rb-4.1.0&q=85";

/**
 * KingdomPoster: CSS/Canvas-style composite poster
 * - Castle background, Princess Anagha at center, guest characters arranged around her
 */
const KingdomPoster = forwardRef(function KingdomPoster({ guests = [], princessSrc, title }, ref) {
  const [info, setInfo] = useState({ princess_name: "Anagha", occasion: "First Birthday", princess_image: null });
  useEffect(() => {
    getKingdomInfo().then(setInfo).catch(() => {});
  }, []);

  const finalPrincessSrc = princessSrc || (info.princess_image ? fileUrl(info.princess_image) : null);
  const finalTitle = title || `The Kingdom of ${info.princess_name}`;
  const top = guests.slice(0, 24);

  return (
    <div
      ref={ref}
      data-testid="kingdom-poster"
      className="relative w-full aspect-[16/10] overflow-hidden rounded-3xl shadow-[0_30px_80px_rgba(74,14,78,0.25)]"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,240,245,0.25), rgba(74,14,78,0.55) 95%), url(${CASTLE_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Sparkles count={40} />

      {/* Title banner */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 text-center px-6 py-3 rounded-full glass-deep">
        <p className="label-eyebrow">A Royal Decree · {info.occasion}</p>
        <h2 className="font-display text-3xl sm:text-5xl font-semibold gold-text leading-tight">
          {finalTitle}
        </h2>
        <p className="font-script text-xl text-blush">turning one · with love</p>
      </div>

      {/* Princess Anagha at center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        <div
          className="relative w-40 h-40 sm:w-56 sm:h-56 rounded-full overflow-hidden ring-4 ring-[#FCF6BA]/80 shadow-[0_0_60px_rgba(252,246,186,0.65)]"
          style={{ background: "radial-gradient(circle, #FFF0F5, #E6E6FA)" }}
        >
          {finalPrincessSrc ? (
            <img src={finalPrincessSrc} alt={`Princess ${info.princess_name}`} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <Crown className="text-[#B38728] animate-float" size={64} />
            </div>
          )}
        </div>
        <p className="mt-3 font-script text-3xl sm:text-4xl text-blush drop-shadow-lg">
          Princess {info.princess_name}
        </p>
        <p className="label-eyebrow text-blush/90">— the guest of honour · age one —</p>
      </div>

      {/* Guest characters arranged around princess */}
      {top.map((g, idx) => {
        const total = top.length;
        const angle = (idx / Math.max(total, 1)) * Math.PI * 2 - Math.PI / 2;
        const radiusX = 38 + (idx % 3) * 3;
        const radiusY = 28 + (idx % 3) * 3;
        const x = 50 + Math.cos(angle) * radiusX;
        const y = 50 + Math.sin(angle) * radiusY;
        return (
          <div
            key={g.id}
            data-testid={`poster-guest-${g.id}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${idx * 0.1}s` }}
          >
            <div
              className="w-14 h-14 sm:w-20 sm:h-20 rounded-full overflow-hidden ring-2 ring-[#D4AF37]/80 shadow-lg animate-float"
              style={{ animationDelay: `${(idx % 5) * 0.4}s` }}
            >
              <img src={fileUrl(g.character_path)} alt={g.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <p className="mt-1 font-script text-sm sm:text-base text-blush drop-shadow">{g.name}</p>
          </div>
        );
      })}

      {/* Birthday brand corner */}
      <div className="absolute bottom-5 right-6 text-right flex items-center gap-3">
        <div>
          <SparklesIcon className="ml-auto text-[#FCF6BA]" size={20} />
          <p className="font-script text-2xl text-blush">Happy 1st Birthday</p>
          <p className="label-eyebrow text-blush/80">Anagha&apos;s Magical Kingdom</p>
        </div>
        <div
          className="w-14 h-16 grid place-items-center rounded-xl shadow-lg"
          style={{ background: "linear-gradient(160deg, #FCF6BA, #D4AF37 60%, #B38728)" }}
        >
          <span className="font-display text-4xl text-plum-deep font-bold">1</span>
        </div>
      </div>
    </div>
  );
});

export default KingdomPoster;
